import {
  Control,
  UseFormRegister,
  FieldErrors,
  FieldValues,
  FieldPath,
  useWatch,
} from 'react-hook-form';

import TimeField from '@/components/TimeField/TimeField';
import {
  calculateTimeDuration,
  calculateTimeMinutes,
  formatMinutesToDuration,
} from '@/logics/supportPlan/supportPlan';

type Props<T extends FieldValues> = {
  errors: FieldErrors<T>;
  control: Control<T>;
  register: UseFormRegister<T>;
  childName: string;
};

// 曜日の定義
const DAYS_OF_WEEK = [
  { id: 'monday', label: '月' },
  { id: 'tuesday', label: '火' },
  { id: 'wednesday', label: '水' },
  { id: 'thursday', label: '木' },
  { id: 'friday', label: '金' },
  { id: 'saturday', label: '土' },
  { id: 'sunday_holiday', label: '日・祝' },
];

export const AppendixForm = <T extends FieldValues>({ control, register, childName }: Props<T>) => {
  const typedRegister = (name: string) => register(name as FieldPath<T>);

  const watchedSchedules = useWatch({
    control,
    name: 'schedules' as FieldPath<T>,
  });

  const getServiceDuration = (dayId: string): string => {
    if (!watchedSchedules || !watchedSchedules[dayId]) return '';

    const schedule = watchedSchedules[dayId];
    return calculateTimeDuration(schedule.service_start_time, schedule.service_end_time);
  };

  const getExtensionDuration = (dayId: string): string => {
    if (!watchedSchedules || !watchedSchedules[dayId]) return '';

    const schedule = watchedSchedules[dayId];

    const preMinutes = calculateTimeMinutes(
      schedule.pre_extension_start_time,
      schedule.pre_extension_end_time
    );

    const postMinutes = calculateTimeMinutes(
      schedule.post_extension_start_time,
      schedule.post_extension_end_time
    );

    const totalMinutes = preMinutes + postMinutes;

    return formatMinutesToDuration(totalMinutes);
  };

  return (
    <div className="mt-6 w-full">
      <h3 className="mb-4 text-lg font-bold">{childName}</h3>

      <div className="w-full overflow-x-auto">
        <table className="w-[1400px] border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2"></th>
              {DAYS_OF_WEEK.map((day) => (
                <th
                  key={day.id}
                  className="border border-gray-300 p-2 text-center"
                >
                  {day.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* 提供時間の行 */}
            <tr>
              <td
                rowSpan={2}
                className="w-32 border border-gray-300 p-2 text-sm"
              >
                提供時間
              </td>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2 text-center"
                >
                  <div className="text-sm">利用開始・終了時間</div>
                </td>
              ))}
            </tr>
            <tr>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <TimeField
                      control={control}
                      name={`schedules.${day.id}.service_start_time` as FieldPath<T>}
                    />
                    <span>〜</span>
                    <TimeField
                      control={control}
                      name={`schedules.${day.id}.service_end_time` as FieldPath<T>}
                    />
                  </div>
                </td>
              ))}
            </tr>

            {/* 提供時間合計 */}
            <tr>
              <td className="border border-gray-300 p-2 text-sm">提供時間合計</td>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2 text-center"
                >
                  <span className="text-sm">{getServiceDuration(day.id) || ''}</span>
                </td>
              ))}
            </tr>

            {/* 延長支援時間帯（前） */}
            <tr>
              <td
                rowSpan={4}
                className="w-32 whitespace-normal border border-gray-300 p-2 align-top text-sm"
              >
                延長支援時間
                <br />
                <span className="text-xs">
                  ※ 延長支援時間は、
                  <br />
                  支援前・支援後
                  <br />
                  それぞれ１時間以上から
                </span>
              </td>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2 text-center"
                >
                  <div className="mb-1 text-center text-sm font-medium">
                    【支援前】
                    <br />
                    延長支援開始時間
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <TimeField
                      control={control}
                      name={`schedules.${day.id}.pre_extension_start_time` as FieldPath<T>}
                    />
                    <span>〜</span>
                    <TimeField
                      control={control}
                      name={`schedules.${day.id}.pre_extension_end_time` as FieldPath<T>}
                    />
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2 text-center"
                >
                  <div className="mb-1 text-center text-sm font-medium">
                    【支援後】
                    <br />
                    延長支援開始時間
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <TimeField
                      control={control}
                      name={`schedules.${day.id}.post_extension_start_time` as FieldPath<T>}
                    />
                    <span>〜</span>
                    <TimeField
                      control={control}
                      name={`schedules.${day.id}.post_extension_end_time` as FieldPath<T>}
                    />
                  </div>
                </td>
              ))}
            </tr>
            {/* 延長時間合計 */}
            <tr>
              <td className="border border-gray-300 p-2 text-sm">延長時間合計</td>
              {DAYS_OF_WEEK.map((day) => (
                <td
                  key={day.id}
                  className="border border-gray-300 p-2 text-center"
                >
                  <span className="text-sm">{getExtensionDuration(day.id) || ''}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <table className="mt-0 w-[1400px] table-fixed border-collapse border border-gray-300">
          <tbody>
            <tr>
              <td className="w-32 whitespace-normal border border-gray-300 p-2 text-sm">
                延長を必要とする理由
              </td>
              <td className="border border-gray-300 p-2">
                <textarea
                  className="w-full rounded-md border border-gray-300 p-2"
                  rows={4}
                  {...typedRegister('extension_reason')}
                ></textarea>
              </td>
            </tr>

            <tr>
              <td className="w-32 justify-center border border-gray-300 p-2 text-sm">特記事項</td>
              <td className="border border-gray-300 p-2">
                <textarea
                  className="w-full rounded-md border border-gray-300 p-2"
                  rows={4}
                  {...typedRegister('special_notes')}
                ></textarea>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
